/**
 * @page /portal/profil
 * @description Mitarbeiter-Profil — Persoenliche Stammdaten, Arbeitszeiten und
 *   Vertragsinformationen des eingeloggten Mitarbeiters.
 * @features
 *   - Persoenliche Daten (Name, SVNR, Kontakt)
 *   - Vertragsdetails und Arbeitszeit-Modell
 *   - Woechentliche Sollarbeitszeiten
 * @api GET /api/portal/profil
 * @roles mitarbeiter, admin, administrator, manager, ops_manager, teamleiter
 * @i18n portal.profil
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Info, User } from 'lucide-react';
import { Card, PageHeader } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';

function formatDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatTime(decimal) {
  if (decimal == null) return '';
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

function InfoRow({ label, value, masked }) {
  return (
    <div className="flex items-start py-2.5 border-b border-line/50 last:border-0">
      <div className="w-40 flex-shrink-0 text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{label}</div>
      <div className={`text-sm ${masked ? 'font-mono text-[var(--text-secondary)]' : 'text-content'}`}>
        {value || '–'}
      </div>
    </div>
  );
}

export default function MaPortalProfil() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/profil`, { credentials: 'include' });
      if (res.ok) {
        setProfil(await res.json());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-page">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-inset rounded w-1/3" />
          <div className="h-64 bg-surface-inset rounded-card" />
        </div>
      </div>
    );
  }

  if (!profil) {
    return (
      <div className="p-page">
        <PageHeader title={t('portal.profile.title')} subtitle={t('portal.profile.subtitle')} className="mb-section" />
        {/* SKILL-design Empty State pattern */}
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl">
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
              <User size={28} className="text-[var(--text-muted)]" />
            </div>
            <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">
              {t('portal.profile.noProfile')}
            </h3>
            <p className="text-sm text-[var(--text-tertiary)] max-w-sm mb-5">
              {t('portal.profile.contactHR')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Arbeitszeiten gruppiert nach Wochentag
  const arbeitszeitenMap = {};
  if (profil.arbeitszeiten) {
    for (const az of profil.arbeitszeiten) {
      if (!arbeitszeitenMap[az.wochentag]) arbeitszeitenMap[az.wochentag] = [];
      arbeitszeitenMap[az.wochentag].push(az);
    }
  }

  const genderDisplay = profil.gender === 'männlich' ? t('portal.profile.genderMale') : profil.gender === 'weiblich' ? t('portal.profile.genderFemale') : profil.gender;

  return (
    <div className="p-page">
      <PageHeader title={t('portal.profile.title')} subtitle={t('portal.profile.subtitle')} className="mb-section" />

      {/* Profil-Karte */}
      <div
        className="bg-surface-card border border-line rounded-card overflow-hidden mb-6"
        style={{ borderTopColor: '#22c55e', borderTopWidth: 3 }}
      >
        <div className="p-6">
          <div className="flex items-start gap-5">
            {/* Avatar */}
            {profil.image_url ? (
              <img src={profil.image_url} alt="" className="w-20 h-20 rounded-card object-cover flex-shrink-0" />
            ) : (
              <div className="w-20 h-20 rounded-card bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-2xl">
                  {(profil.vorname?.[0] || '').toUpperCase()}{(profil.nachname?.[0] || '').toUpperCase()}
                </span>
              </div>
            )}

            {/* Name & Badges */}
            <div className="flex-1">
              <h2 className="text-xl font-bold text-content">{profil.vorname} {profil.nachname}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                {profil.berufsbezeichnung && (
                  <span className="px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 text-xs font-medium">
                    {profil.berufsbezeichnung}
                  </span>
                )}
                {profil.department_name && (
                  <span className="px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-400 text-xs font-medium">
                    {profil.department_name}
                  </span>
                )}
                {profil.standort_name && (
                  <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 text-xs font-medium">
                    {profil.standort_name}
                  </span>
                )}
              </div>
              {profil.kuerzel && (
                <span className="inline-block mt-2 px-2 py-0.5 rounded bg-surface-inset text-xs font-mono text-[var(--text-tertiary)]">
                  {t('portal.profile.abbreviation', { code: profil.kuerzel })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Persönliche Daten */}
      <Card title={t('portal.profile.personalData')} className="mb-section">
        <InfoRow label={t('portal.profile.svnr')} value={profil.svnr_masked} masked />
        <InfoRow label={t('portal.profile.birthday')} value={formatDate(profil.birthday)} />
        <InfoRow label={t('portal.profile.gender')} value={genderDisplay} />
        <InfoRow label={t('portal.profile.address')} value={profil.x_studio_zip_city} />
        <InfoRow label={t('portal.profile.phoneMobile')} value={profil.mobile_phone} />
        <InfoRow label={t('portal.profile.phoneWork')} value={profil.work_phone} />
        <InfoRow label={t('portal.profile.emailWork')} value={profil.work_email} />
      </Card>

      {/* Berufliche Daten */}
      <Card title={t('portal.profile.professionalData')} className="mb-section">
        <InfoRow label={t('portal.profile.position')} value={profil.berufsbezeichnung} />
        <InfoRow label={t('portal.profile.department')} value={profil.department_name} />
        <InfoRow label={t('portal.profile.location')} value={profil.standort_name} />
        <InfoRow label={t('portal.profile.startDate')} value={formatDate(profil.vertrag_start)} />
        <InfoRow label={t('portal.profile.contractType')} value={profil.vertrag_typ} />
        <InfoRow label={t('portal.profile.supervisor')} value={profil.parent_employee_name} />
        <InfoRow label={t('portal.profile.coach')} value={profil.coach_name} />
        <InfoRow label={t('portal.profile.workTimeModel')} value={profil.arbeitszeitmodell} />
        <InfoRow label={t('portal.profile.hoursPerWeek')} value={profil.stunden_pro_woche ? `${profil.stunden_pro_woche}h` : '–'} />
      </Card>

      {/* Arbeitszeiten pro Wochentag */}
      {Object.keys(arbeitszeitenMap).length > 0 && (
        <Card title={t('portal.profile.workingHours')} className="mb-section">
          <div className="grid gap-2">
            {[0, 1, 2, 3, 4, 5, 6].map(tag => {
              const slots = arbeitszeitenMap[tag];
              if (!slots) return null;
              const totalSoll = slots.reduce((s, sl) => s + parseFloat(sl.soll_stunden || 0), 0);
              return (
                <div key={tag} className="flex items-center gap-3 py-1.5 border-b border-line/30 last:border-0">
                  <div className="w-24 text-xs font-semibold text-[var(--text-secondary)]">{t(`portal.profile.weekdays.${tag}`)}</div>
                  <div className="flex-1 flex flex-wrap gap-3 text-xs text-[var(--text-secondary)]">
                    {slots.map((sl, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-surface-inset">
                        {formatTime(sl.stunden_von)} – {formatTime(sl.stunden_bis)}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs font-mono text-content-secondary">
                    {totalSoll.toFixed(1)}h
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Hinweis */}
      <div className="bg-surface-inset border border-line rounded-card p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-primary-400 flex-shrink-0" />
        <div className="text-xs text-[var(--text-tertiary)]">
          <p className="font-semibold text-[var(--text-secondary)] mb-1">{t('portal.profile.dataWrong')}</p>
          <p>{t('portal.profile.dataWrongDesc')}</p>
        </div>
      </div>
    </div>
  );
}
